<!--
  Copyright (C) 2023 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="member-list">
    <div v-if="isSite" class="member-list__head">
      <h3>
        <span
          >{{ $t("members.youAre") }}
          <span class="member-list__role">{{ role }}</span>
          {{ $t("members.ofThisWorkspace") }}.</span
        >
      </h3>
    </div>
    <div v-else class="member-list__head member-list__head--folder">
      <div>
        {{
          $t("members.nbAccess", {
            nbAccess: nbMembers,
          })
        }}
      </div>
      <div>
        <PrimeButton
          v-if="isHerited"
          :label="$t('members.restrictPermissions')"
          rounded
          :disabled="!permissions"
          severity="warning"
          size="large"
          icon="ri-prohibited-line"
          @click="restrictPermissions()"
        />
        <PrimeButton
          v-else
          :label="$t('members.restorePermissions')"
          rounded
          :disabled="!permissions"
          size="large"
          icon="ri-refresh-line"
          @click="restorePermissions()"
        />
      </div>
    </div>
    <div>
      <PrimeDataTable
        v-if="isSite"
        id="membres"
        :value="sortableMembers"
        row-hover
        data-key="id"
        removable-sort
      >
        <PrimeColumn field="avatar">
          <template #body="slotProps">
            <PrimeAvatar
              :label="
                slotProps.data.person
                  ? slotProps.data.person.displayName.charAt(0)
                  : slotProps.data.group.displayName.charAt(0)
              "
              size="large"
              class="avatar-blue"
              shape="circle"
            />
          </template>
        </PrimeColumn>
        <PrimeColumn
          field="isGroup"
          :header="$t('groups')"
          header-style="max-width: 4rem"
          sortable
        >
          <template #body="slotProps">
            <i v-if="slotProps.data.isGroup" class="ri-group-line members" />
          </template>
        </PrimeColumn>
        <PrimeColumn
          field="person"
          :header="$t('members.members')"
          sortable
          sort-field="displayName"
        >
          <template #body="slotProps">
            <template v-if="slotProps.data.person">
              <div>
                <button
                  type="button"
                  class="member-profile-btn"
                  :aria-label="$t('members.viewUserProfile')"
                  :title="$t('members.viewUserProfile')"
                  @click="openUserProfile(slotProps.data.id)"
                >
                  <i class="ri-user-line" aria-hidden="true" />
                </button>
                <span>{{ slotProps.data.person.displayName }}</span>
              </div>
              <div class="member-details">
                {{ slotProps.data.id }} - {{ slotProps.data.person.email }}
              </div>
            </template>
            <span v-else>{{ slotProps.data.group.displayName }}</span>
          </template>
        </PrimeColumn>
        <PrimeColumn field="role" :header="$t('members.role')">
          <template #body="slotProps">
            <PrimeSelect
              v-model="selectedRole[slotProps.data.id]"
              :options="rolesValues"
              option-label="name"
              :placeholder="$t(`role.${slotProps.data.role}`)"
              :value="slotProps.data.role"
              :disabled="!permissions"
              @change="toggleChangeMemberRole(slotProps.data)"
            />
          </template>
        </PrimeColumn>
        <PrimeColumn field="buttons">
          <template #body="slotProps">
            <PrimeButton
              type="button"
              icon="ri-close-line"
              :aria-label="$t('delete')"
              :disabled="!permissions"
              severity="danger"
              rounded
              text
              @click="toggleDeleteMember(slotProps.data)"
            />
          </template>
        </PrimeColumn>
      </PrimeDataTable>

      <PrimeDataTable
        v-else
        id="membres"
        :value="members"
        row-hover
        data-key="id"
        removable-sort
      >
        <PrimeColumn field="avatar">
          <template #body="slotProps">
            <PrimeAvatar
              v-if="!slotProps.data.authorityId.startsWith('GROUP')"
              :label="slotProps.data.authorityId.charAt(0)"
              size="large"
              class="avatar-blue"
              shape="circle"
            />
            <i v-else class="ri-group-line members" />
          </template>
        </PrimeColumn>
        <PrimeColumn field="person" :header="$t('members.members')">
          <template #body="slotProps">
            <span>{{ slotProps.data.authorityId }}</span>
            <p v-if="slotProps.data.groupId" class="member-list__description">
              {{
                $t(`members.fromGroup`, {
                  group: slotProps.data.groupId,
                })
              }}
            </p>
          </template>
        </PrimeColumn>
        <PrimeColumn field="role" :header="$t('members.role')">
          <template #body="slotProps">
            <PrimeSelect
              v-if="!slotProps.data.groupId"
              v-model="selectedRole[slotProps.data.name]"
              :options="rolesValues"
              option-label="name"
              :placeholder="$t(`role.${slotProps.data.name}`)"
              :value="slotProps.data.name"
              :disabled="!permissions"
              @change="changePermission(slotProps.data)"
            />
            <div v-else>
              {{ $t(`role.${slotProps.data.name}`) }}
            </div>
          </template>
        </PrimeColumn>
        <PrimeColumn field="buttons">
          <template #body="slotProps">
            <PrimeButton
              v-if="!slotProps.data.groupId"
              type="button"
              icon="ri-close-line"
              :aria-label="$t('delete')"
              :disabled="!permissions"
              severity="danger"
              rounded
              text
              @click="deletePermission(slotProps.data)"
            />
          </template>
        </PrimeColumn>
      </PrimeDataTable>
      <div class="member-list__add-row">
        <div>
          <PrimeAutoComplete
            v-model="selectedMemberToAdd"
            force-selection
            option-label="label"
            :suggestions="filteredMembers"
            :placeholder="$t('members.search')"
            :disabled="!permissions"
            @complete="searchUser($event)"
          >
            <template #item="slotProps">
              <div class="member-item">
                <i v-if="slotProps.item.group" class="ri-group-line" />
                <i v-else class="ri-user-line" />
                <div class="member-item__label">{{ slotProps.item.label }}</div>
              </div>
            </template>
          </PrimeAutoComplete>
        </div>
        <div>
          <PrimeSelect
            v-model="addSelectedRole"
            :options="rolesValues"
            option-label="name"
            :placeholder="$t('members.select')"
            :disabled="!permissions"
          />
        </div>
        <div>
          <PrimeButton
            v-if="isSite"
            type="button"
            icon="ri-add-line"
            :label="$t('members.add')"
            :disabled="!permissions"
            @click="toggleAddMember"
          />
          <PrimeButton
            v-else
            type="button"
            icon="ri-add-line"
            :label="$t('members.add')"
            :disabled="!permissions"
            @click="addPermissions()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import workspaceService from "@/services/WorkspaceService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import { useConfigStore } from "@/stores/config.js";
import { mapState } from "pinia";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Avatar from "primevue/avatar";
import Select from "primevue/select";
import AutoComplete from "primevue/autocomplete";

export default {
  name: "MemberList",
  components: {
    PrimeButton: Button,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimeAvatar: Avatar,
    PrimeSelect: Select,
    PrimeAutoComplete: AutoComplete,
  },
  props: {
    members: {
      type: Array,
      default: () => [],
    },
    workspace: {
      type: Object,
      default: () => ({}),
    },
    permissions: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: "",
    },
    isSite: {
      type: Boolean,
      default: true,
    },
    isHerited: {
      type: Boolean,
      default: true,
    },
    nbMembers: {
      type: Number,
      default: 0,
    },
    membersWithoutGroups: {
      type: Array,
      default: () => [],
    },
    membersLocallySet: {
      type: Object,
      default: () => ({}),
    },
    folderNode: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["reload", "update-permission"],
  computed: {
    ...mapState(useConfigStore, ["PORTAIL_HOST"]),
    sortableMembers() {
      if (!this.members) {
        return [];
      }
      return this.members.map((member) => ({
        ...member,
        displayName: member.person
          ? member.person.displayName
          : member.group?.displayName || "",
      }));
    },
  },
  data() {
    return {
      selectedRole: [],
      rolesValues: [
        { name: this.$t("role.SiteManager") },
        { name: this.$t("role.SiteCollaborator") },
        { name: this.$t("role.SiteConsumer") },
      ],
      defaultDropDownValue: { name: this.$t("role.SiteConsumer") },
      selectedMemberToAdd: null,
      filteredMembers: [],
      addSelectedRole: null,
      heredityValue: null,
    };
  },
  mounted() {
    this.addSelectedRole = this.defaultDropDownValue;
    this.heredityValue = this.isHerited;
  },
  methods: {
    openUserProfile(userId) {
      const portailHost = this.PORTAIL_HOST || "";
      window.open(`${portailHost}/portail/user/${userId}`, "_blank");
    },
    toggleAddMember() {
      if (this.selectedMemberToAdd == null) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("members.errorAdd"),
          life: 3000,
        });
      } else {
        let newRole = this.getAlfrescoRole(this.addSelectedRole.name);
        let isGroup = this.selectedMemberToAdd.group;
        const addMember = isGroup
          ? workspaceService.addWorkspaceGroup(
              this.workspace.id,
              this.selectedMemberToAdd.id,
              newRole,
            )
          : workspaceService.addWorkspaceUser(
              this.workspace.id,
              this.selectedMemberToAdd.id,
              newRole,
            );
        addMember
          .then(() => {
            this.$toast.add({
              severity: "success",
              summary: this.$t("success"),
              detail: this.$t("members.successAdd"),
              life: 3000,
            });
            this.selectedMemberToAdd = null;
            this.addSelectedRole = this.defaultDropDownValue;
            this.$emit("reload");
          })
          .catch((err) => {
            this.$toast.add({
              severity: "error",
              summary: this.$t("error"),
              detail: err.message,
              life: 3000,
            });
          });
      }
    },
    toggleChangeMemberRole(member) {
      let siteId = this.workspace.id;
      let newRole = this.getAlfrescoRole(this.selectedRole[member.id].name);
      const changeMemberRole = member.group
        ? workspaceService.updateWorkspaceGroupRole(siteId, member.id, newRole)
        : workspaceService.updateWorkspaceUserRole(siteId, member.id, newRole);
      changeMemberRole
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("members.successChangeRole"),
            life: 3000,
          });
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    toggleDeleteMember(member) {
      let siteId = this.workspace.id;
      const deleteMember = member.group
        ? workspaceService.deleteWorkspaceGroup(siteId, member.id)
        : workspaceService.deleteWorkspaceUser(siteId, member.id);
      deleteMember
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("members.successDelete", { id: member.id }),
            life: 3000,
          });
        })
        .then(() => {
          this.$emit("reload");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    getAlfrescoRole(role) {
      return Object.keys(this.$tm("role")).find(
        (keyRole) => this.$tm("role")[keyRole] === role,
      );
    },
    searchUser(event) {
      const term = event.query.trim();
      if (term.length < 2) {
        this.filteredMembers = [];
        return;
      }
      Promise.all([
        workspaceService.searchUsers(term),
        workspaceService.searchGroups(term),
      ]).then(([users, groups]) => {
        this.filteredMembers = [...users, ...groups];
      });
    },
    restrictPermissions() {
      this.heredityValue = false;
      this.changeHeredity();
    },
    restorePermissions() {
      this.heredityValue = true;
      this.changeHeredity();
    },
    changeHeredity() {
      const opts = {
        permissions: {
          locallySet: [],
          isInheritanceEnabled: this.heredityValue,
        },
      };
      alfrescoNodeService
        .updateNode(this.folderNode.id, opts)
        .then(() => {
          const detailString = this.heredityValue
            ? this.$t("members.successRestorePermissions")
            : this.$t("members.successRestrictPermissions");
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: detailString,
            life: 3000,
          });
          this.$emit("update-permission");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    addPermissions() {
      if (this.selectedMemberToAdd == null) {
        this.$toast.add({
          severity: "error",
          summary: this.$t("error"),
          detail: this.$t("members.errorAdd"),
          life: 3000,
        });
      } else {
        const newRole = this.getAlfrescoRole(this.addSelectedRole.name);
        const nextIndex = Object.keys(this.membersWithoutGroups).length + 1;
        const obj = {
          locallySet: {
            ...this.membersWithoutGroups,
            [nextIndex]: {
              accessStatus: "ALLOWED",
              authorityId: this.selectedMemberToAdd.id,
              name: newRole,
            },
          },
        };
        let opts = {
          permissions: {
            locallySet: Object.values(obj.locallySet).map((item) => ({
              authorityId: item.authorityId,
              name: item.name,
              accessStatus: item.accessStatus,
            })),
          },
        };
        alfrescoNodeService
          .updateNode(this.folderNode.id, opts)
          .then(() => {
            this.$toast.add({
              severity: "success",
              summary: this.$t("success"),
              detail: this.$t("members.successAdd"),
              life: 3000,
            });
            this.$emit("update-permission");
          })
          .catch((err) => {
            this.$toast.add({
              severity: "error",
              summary: this.$t("error"),
              detail: err.message,
              life: 3000,
            });
          });
      }
    },
    deletePermission(item) {
      let updatedMembersLocallySet = [...this.membersLocallySet];
      const indexToRemove = updatedMembersLocallySet.findIndex((userObj) => {
        return userObj.authorityId === item.authorityId;
      });

      if (indexToRemove !== -1) {
        updatedMembersLocallySet.splice(indexToRemove, 1);
      }

      let opts = {
        permissions: {
          locallySet: updatedMembersLocallySet,
        },
      };
      alfrescoNodeService
        .updateNode(this.folderNode.id, opts)
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("members.successDelete", { id: item.authorityId }),
            life: 3000,
          });
          this.$emit("update-permission");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    changePermission(item) {
      let updatedMembersLocallySet = [...this.membersLocallySet];
      for (let i = 0; i < updatedMembersLocallySet.length; i++) {
        const userObj = updatedMembersLocallySet[i];

        if (userObj.authorityId === item.authorityId) {
          userObj.name = this.getAlfrescoRole(
            this.selectedRole[item.name].name,
          );
          break;
        }
      }
      let opts = {
        permissions: {
          locallySet: updatedMembersLocallySet,
        },
      };
      alfrescoNodeService
        .updateNode(this.folderNode.id, opts)
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("members.successChangeRole"),
            life: 3000,
          });
          this.$emit("update-permission");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message,
            life: 3000,
          });
        });
    },
  },
};
</script>

<style lang="css" scoped>
.member-list__head {
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.member-list__head--folder {
  justify-content: space-between;
}

.member-list__role {
  font-weight: 500;
}

.member-list__description {
  font-size: 0.85em;
  color: var(--p-text-muted-color, #6c757d);
}

.member-list__add-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.member-item {
  display: inline-flex;
  align-items: center;
}

.member-item__label {
  margin-left: 0.5rem;
}

.members {
  display: flex;
  justify-content: center;
}

.avatar-blue {
  background-color: #2196f3;
  color: #ffffff;
}

.member-details {
  margin-top: 0.25rem;
  font-size: 0.85em;
  color: var(--p-text-muted-color, #6c757d);
}

.member-profile-btn {
  background: none;
  border: none;
  padding: 0;
  margin-right: 0.5rem;
  cursor: pointer;
  color: var(--p-primary-color, #2196f3);
  font-size: inherit;
  line-height: inherit;
  vertical-align: middle;
}

.member-profile-btn:hover {
  opacity: 0.8;
}

.member-profile-btn:focus-visible {
  outline: 2px solid var(--p-primary-color, #2196f3);
  outline-offset: 2px;
  border-radius: 2px;
}
</style>
